import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { getCache, setCache, hasCache } from "../lib/swr";
import { useAuth } from "../context/AuthContext";
import DispatchCard from "../components/DispatchCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Plus, Loader2, MessageSquare, Clock } from "lucide-react";
import MediaUploader from "../components/MediaUploader";
import { GrowTextarea, LinkFields } from "../components/DispatchFields";
import { EmptyState } from "../components/EmptyState";
import { SectionBanner } from "../components/SectionBanner";

export default function Feed() {
  const { user, can, org } = useAuth();
  const orgCommentsOn = org?.comments_enabled !== false;
  const [items, setItems] = useState(() => getCache("feed:dispatches") || []);
  const [loading, setLoading] = useState(() => !hasCache("feed:dispatches"));
  const [open, setOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", country: "", media: [], videos: [], video_url: "", link_url: "", link_label: "", is_prayer: false, publish_at: "", send_email: false, allow_comments: orgCommentsOn });

  const load = async () => {
    try { const { data } = await api.get("/dispatches"); setItems(data); setCache("feed:dispatches", data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and message required");
    setPosting(true);
    try {
      await api.post("/dispatches", { ...form, publish_at: form.publish_at || undefined });
      const isFuture = form.publish_at && new Date(form.publish_at) > new Date();
      setOpen(false);
      setForm({ title: "", body: "", country: "", media: [], videos: [], video_url: "", link_url: "", link_label: "", is_prayer: false, publish_at: "", send_email: false, allow_comments: orgCommentsOn });
      await load();
      toast.success(isFuture ? "Dispatch scheduled" : "Dispatch posted!");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPosting(false); }
  };

  return (
    <div>
      <SectionBanner title="Dispatches" subtitle="Stories from the journey" icon={MessageSquare} accent="#60A5FA">
        {can("news") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-dispatch-btn" className="rounded-full bg-white text-[#0d2340] hover:bg-white/90 shrink-0">
                <Plus className="h-4 w-4 mr-1" /> Post
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-w-lg max-h-[92dvh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">New dispatch</DialogTitle><DialogDescription className="sr-only">Share an update with your partners</DialogDescription></DialogHeader>
              <div className="space-y-4 mt-2">
                <Input data-testid="dispatch-title-input" placeholder="Title" className="rounded-xl h-11"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <GrowTextarea testid="dispatch-body-input" value={form.body} onChange={(v) => setForm((f) => ({ ...f, body: v }))} />
                <Input data-testid="dispatch-country-input" placeholder="Country (optional)" className="rounded-xl h-11"
                  value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                <Input data-testid="dispatch-video-input" placeholder="Video URL — YouTube, Vimeo (optional)" className="rounded-xl h-11"
                  value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
                <MediaUploader media={form.media} videos={form.videos} onChange={({ media, videos }) => setForm((f) => ({ ...f, media, videos }))} />
                <LinkFields form={form} setForm={setForm} prefix="dispatch" />
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Mark as prayer request</Label>
                  <Switch data-testid="dispatch-prayer-switch" checked={form.is_prayer} onCheckedChange={(v) => setForm({ ...form, is_prayer: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Allow comments</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Off hides comments on this post (reactions stay). Protects sensitive regions.</p>
                  </div>
                  <Switch data-testid="dispatch-comments-switch" checked={form.allow_comments} onCheckedChange={(v) => setForm({ ...form, allow_comments: v })} />
                </div>
                <div className="flex items-center justify-between rounded-xl bg-[var(--brand-navy)]/5 px-3 py-2.5">
                  <div>
                    <Label className="text-sm">Also send as email</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Emails this dispatch to all partners (in addition to the app).</p>
                  </div>
                  <Switch data-testid="dispatch-email-switch" checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: v })} />
                </div>
                <div>
                  <Label className="text-sm">Publish date &amp; time <span className="text-muted-foreground">(optional)</span></Label>
                  <Input data-testid="dispatch-publish-input" type="datetime-local" className="rounded-xl h-11 mt-1"
                    value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Leave blank to post now. Pick a <strong>past</strong> date to back-date, or a <strong>future</strong> date/time to schedule (partners won't see it until then).
                  </p>
                </div>
                <Button data-testid="dispatch-submit-btn" disabled={posting} onClick={post}
                  className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">
                  {posting ? "Posting…" : (form.publish_at && new Date(form.publish_at) > new Date() ? "Schedule dispatch" : "Post dispatch")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </SectionBanner>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>
      ) : items.length === 0 ? (
        can("news") ? (
          <EmptyState
            testid="feed-empty"
            icon={MessageSquare}
            title="Share your first dispatch"
            description="Post a photo, a story or an update from the field — your partners will see it right away."
            actionLabel="Post a dispatch"
            onAction={() => setOpen(true)}
          />
        ) : (
          <EmptyState
            testid="feed-empty"
            icon={MessageSquare}
            title="No dispatches yet"
            description="Updates from the field will appear here. Check back soon to follow the journey."
          />
        )
      ) : (
        <div className="flex flex-col gap-6">
          {can("news") && items.some((d) => d.scheduled) && (
            <div data-testid="scheduled-section" className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3">
              <div className="flex items-center gap-2 px-1 pb-2 text-amber-800">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-semibold">Scheduled · {items.filter((d) => d.scheduled).length}</span>
                <span className="text-xs text-amber-700/70">— visible to partners at their publish time</span>
              </div>
              <div className="flex flex-col gap-4">
                {items.filter((d) => d.scheduled).map((d) => (
                  <DispatchCard key={d.id} dispatch={d}
                    onDelete={(id) => setItems((prev) => prev.filter((x) => x.id !== id))} />
                ))}
              </div>
            </div>
          )}
          {items.filter((d) => !d.scheduled).map((d) => (
            <DispatchCard key={d.id} dispatch={d}
              onDelete={(id) => setItems((prev) => prev.filter((x) => x.id !== id))} />
          ))}
        </div>
      )}
    </div>
  );
}
